import React, { useState, useEffect, useRef } from 'react';
import styles from './ScrimRoundNavigation.module.css';
import { Round } from '../pages/Scrim';

interface ScrimRoundNavigationProps {
  rounds: Round[];
  selectedMap: number | null;
  selectedRound: number | null;
  onRoundChange: (round: Round) => void;
  isProcessing: boolean; // whether the scrim is still processing
  isLastMap: boolean;    // whether the selected map is the last map
}

// Helper function to convert seconds to h:mm:ss format
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const ScrimRoundNavigation: React.FC<ScrimRoundNavigationProps> = ({
  rounds,
  selectedMap,
  selectedRound,
  onRoundChange,
  isProcessing,
  isLastMap
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [roundsPerPage, setRoundsPerPage] = useState(12);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter rounds for the selected map and sort by round number
  const mapRounds = selectedMap !== null 
    ? rounds
        .filter(round => round.map_index === selectedMap)
        .sort((a, b) => a.round_number - b.round_number)
    : [];

  // Create a lookup map for quick access to round data by round number
  const roundLookup = new Map<number, Round>();
  mapRounds.forEach(round => {
    roundLookup.set(round.round_number, round);
  });

  // Find the maximum round number to determine how many slots we need
  const maxRoundNumber = mapRounds.length > 0 
    ? Math.max(...mapRounds.map(round => round.round_number))
    : 0;

  // Determine how many slots to display including placeholders
  // - If fewer than 12 rounds: fill placeholders up to 12
  // - If 12 or more AND last map AND processing: fill placeholders up to next multiple of 12 (24, 36, ...)
  const targetSlots = (() => {
    const count = maxRoundNumber;
    if (count < 12) return 12;
    if (isLastMap && isProcessing) {
      const isExactMultiple = count % 12 === 0;
      const base = Math.ceil(count / 12) * 12;
      return isExactMultiple ? base + 12 : base;
    }
    return count;
  })();

  // Calculate how many rounds fit in the container (based on visual width), independent of how many real rounds we have
  useEffect(() => {
    const calculateRoundsPerPage = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.clientWidth;
      const roundButtonWidth = 32; // Width of each round button
      const roundButtonGap = 5; // Gap between buttons
      const separatorWidth = 21; // Width of separator (1px + 10px margin on each side)

      let roundsThatFit = 0;
      let currentWidth = 0;

      // Simulate adding buttons until we run out of space
      for (let i = 0; i < targetSlots; i++) {
        currentWidth += roundButtonWidth + roundButtonGap;
        // Add separator width every 12
        if ((i + 1) % 12 === 0 && (i + 1) !== targetSlots) {
          currentWidth += separatorWidth;
        }
        if (currentWidth > containerWidth) {
          break;
        }
        roundsThatFit++;
      }

      setRoundsPerPage(Math.max(1, Math.min(roundsThatFit, 24)));
    };

    calculateRoundsPerPage();
    window.addEventListener('resize', calculateRoundsPerPage);
    return () => window.removeEventListener('resize', calculateRoundsPerPage);
  }, [targetSlots]);

  // Reset to first page when map changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedMap]);

  // Calculate total pages based on target slots (including placeholders)
  const totalPages = roundsPerPage > 0 ? Math.ceil(targetSlots / roundsPerPage) : 1;

  // Calculate which indices to show on current page
  const startIndex = currentPage * roundsPerPage;
  const endIndex = Math.min(startIndex + roundsPerPage, targetSlots);

  // Don't render if "Full video" is selected
  if (selectedMap === null) {
    return null;
  }

  return (
    <div className={styles.roundNavigation} ref={containerRef}>
      {currentPage > 0 && (
        <button
          className={styles.pageButton}
          onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
          title="Previous page"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
      )}
      <div className={styles.roundButtonsContainer}>
        {Array.from({ length: endIndex - startIndex }).map((_, idx) => {
          const globalIndex = startIndex + idx; // zero-based index within all slots
          const roundNumber = globalIndex + 1;
          const round = roundLookup.get(roundNumber); // Get actual round data if it exists
          const isReal = round !== undefined; // True if round exists in data
          const isMissing = globalIndex < maxRoundNumber && !isReal; // Missing round within max range
          const isPlaceholder = globalIndex >= maxRoundNumber; // Future round (processing)
          const isSelected = isReal && selectedRound === round.round_number;

          return (
            <React.Fragment key={`slot-${selectedMap}-${roundNumber}`}>
              {isReal ? (
                <button
                  onClick={() => onRoundChange(round)}
                  className={`${styles.roundButton} ${isSelected ? styles.active : ''}`}
                  title={`Round ${roundNumber} (${formatTime(round.round_start)} - ${formatTime(round.round_end)})`}
                >
                  {roundNumber}
                </button>
              ) : isMissing ? (
                <div
                  className={`${styles.roundButton} ${styles.disabled}`}
                  title={`Round ${roundNumber} (missing)`}
                >
                  {roundNumber}
                </div>
              ) : (
                <div
                  className={`${styles.roundButton} ${styles.placeholder}`}
                  title={`Round ${roundNumber} (processing)`}
                >
                  <span className={styles.placeholderSpinner} />
                </div>
              )}
              {(globalIndex + 1) % 12 === 0 && (globalIndex + 1) !== targetSlots && (
                <div className={styles.roundSeparator} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {currentPage < totalPages - 1 && (
        <button
          className={styles.pageButton}
          onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
          title="Next page"
        >
          <i className="bi bi-arrow-right"></i>
        </button>
      )}
    </div>
  );
};

export default ScrimRoundNavigation; 